import { GlobalWorkerOptions, version } from 'pdfjs-dist/legacy/build/pdf.mjs';

/**
 * Configurable worker / asset URLs for pdf.js.
 *
 * The defaults pull from jsDelivr so the viewer works out of the box with any
 * bundler (Turbopack, webpack). Apps with strict CSP, air-gapped deployments,
 * or jsDelivr blocks should override these via `configurePdf()` before
 * mounting the viewer.
 */
export type PdfRuntimeConfig = {
  /** URL to `pdf.worker.min.mjs`. */
  workerSrc: string;
  /** URL prefix for CMaps (used for non-Latin / CJK PDFs). Must end with `/`. */
  cMapUrl: string;
  /** URL prefix for standard fonts. Must end with `/`. */
  standardFontDataUrl: string;
  /** URL prefix for PDF.js WASM decoders (e.g. JPEG 2000). Must end with `/`. */
  wasmUrl: string;
};

const defaults: PdfRuntimeConfig = {
  workerSrc: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${version}/legacy/build/pdf.worker.min.mjs`,
  cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${version}/cmaps/`,
  standardFontDataUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${version}/standard_fonts/`,
  wasmUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${version}/wasm/`,
};

let current: PdfRuntimeConfig = defaults;
let workerInitialized = false;

/**
 * Override the default pdf.js asset URLs. Call this once at app startup —
 * before any `<PdfViewer>` mounts — to point at self-hosted assets.
 *
 * Per-instance overrides (via `<PdfViewer workerSrc=... />`) are also honored
 * and take precedence over this global config.
 */
export function configurePdf(config: Partial<PdfRuntimeConfig>): void {
  current = { ...current, ...config };
  // Re-apply the worker source if it changed and we already initialized.
  if (workerInitialized && config.workerSrc) {
    GlobalWorkerOptions.workerSrc = current.workerSrc;
  }
}

/** Get the currently active PDF runtime config (with any `configurePdf` overrides). */
export function getPdfRuntimeConfig(): PdfRuntimeConfig {
  return current;
}

/**
 * Initialize the pdf.js worker on the current global. Idempotent and SSR-safe:
 * does nothing on the server, does nothing if already initialized.
 *
 * Called from `usePdfDocument`; consumers should not need to invoke directly.
 */
export function ensurePdfWorker(workerSrcOverride?: string): void {
  if (typeof window === 'undefined') return;
  if (workerInitialized && !workerSrcOverride) return;
  GlobalWorkerOptions.workerSrc = workerSrcOverride ?? current.workerSrc;
  workerInitialized = true;
}
